import type { ConventionEmployee } from '../types/convention';
import type { JobOffer } from '../types/jobOffer';

export interface OfferMatch {
  offer: JobOffer;
  score: number;
  reasons: string[];
  warnings: string[];
}

interface TokenIndex {
  tokens: Set<string>;
  prefixes: Set<string>;
}

export interface PreparedOffer {
  offer: JobOffer;
  title: TokenIndex;
  content: TokenIndex;
  locationNorm: string;
  locationTokens: Set<string>;
  locationDept: string | null;
}

const STOPWORDS = new Set([
  'les', 'des', 'une', 'pour', 'avec', 'dans', 'sur', 'par', 'aux', 'est', 'qui', 'que', 'son', 'ses', 'leur',
  'vous', 'nous', 'notre', 'votre', 'cdi', 'cdd', 'interim', 'temps', 'plein', 'partiel',
  'poste', 'emploi', 'offre', 'entreprise', 'societe',
]);

const TITLE_WEIGHT = 50;
const LOCATION_WEIGHT = 30;
const CONTENT_WEIGHT = 20;
const PREFIX_LENGTH = 6;

function normalize(text: string): string {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function tokenize(text: string): string[] {
  const tokens = normalize(text)
    .split(' ')
    .filter(t => t.length >= 3 && !STOPWORDS.has(t) && !/^\d+$/.test(t))
    .map(t => (t.length > 4 ? t.replace(/(eurs|euses|euse|eur|ices|ice|s|x)$/, '') : t));
  return Array.from(new Set(tokens));
}

function buildIndex(text: string): TokenIndex {
  const tokens = new Set(tokenize(text));
  const prefixes = new Set<string>();
  tokens.forEach(t => { if (t.length >= PREFIX_LENGTH) prefixes.add(t.slice(0, PREFIX_LENGTH)); });
  return { tokens, prefixes };
}

function overlap(profile: string[], index: TokenIndex): string[] {
  return profile.filter(p =>
    index.tokens.has(p) || (p.length >= PREFIX_LENGTH && index.prefixes.has(p.slice(0, PREFIX_LENGTH)))
  );
}

function extractDepartement(text: string): string | null {
  const match = (text || '').match(/\b(2[AB]|97\d|\d{2})(?:\d{3})?\b/i);
  return match ? match[1].toUpperCase() : null;
}

export function prepareOffers(offers: JobOffer[]): PreparedOffer[] {
  return offers
    .filter(o => o.status !== 'filled' && o.status !== 'closed')
    .map(offer => ({
      offer,
      title: buildIndex(`${offer.title} ${offer.aiMatchedPoste || ''}`),
      content: buildIndex([offer.description, ...(offer.skills || []), ...(offer.requirements || [])].join(' ')),
      locationNorm: normalize(offer.location || ''),
      locationTokens: new Set(tokenize(offer.location || '')),
      locationDept: extractDepartement(offer.location || ''),
    }));
}

interface EmployeeProfile {
  posteTokens: string[];
  projetTokens: string[];
  allTokens: string[];
  villeDept: string;
  dept: string | null;
  cityTokens: string[];
}

function buildProfile(employee: ConventionEmployee): EmployeeProfile {
  const posteTokens = tokenize(employee.poste_actuel || '');
  const projetTokens = tokenize(employee.projet_valide || '');
  const villeDept = employee.ville_dept || '';
  return {
    posteTokens,
    projetTokens,
    allTokens: Array.from(new Set([...posteTokens, ...projetTokens])),
    villeDept,
    dept: extractDepartement(villeDept),
    cityTokens: tokenize(villeDept.replace(/\d+/g, '')),
  };
}

function scoreLocation(profile: EmployeeProfile, prepared: PreparedOffer): { points: number; reason?: string; warning?: string } {
  if (!profile.villeDept.trim()) return { points: LOCATION_WEIGHT / 3, warning: 'Localisation du salarié non renseignée' };
  if (!prepared.locationNorm) return { points: LOCATION_WEIGHT / 3, warning: 'Lieu de l\'offre non précisé' };

  if (profile.cityTokens.length > 0 && profile.cityTokens.every(t => prepared.locationTokens.has(t))) {
    return { points: LOCATION_WEIGHT, reason: 'Même ville' };
  }
  if (profile.dept && prepared.locationDept && profile.dept === prepared.locationDept) {
    return { points: LOCATION_WEIGHT * 0.85, reason: `Même département (${profile.dept})` };
  }
  if (prepared.locationNorm.includes('teletravail') || prepared.locationNorm.includes('remote')) {
    return { points: LOCATION_WEIGHT * 0.7, reason: 'Télétravail possible' };
  }
  if (profile.dept && prepared.locationDept) return { points: 0, warning: `Département différent (${prepared.locationDept})` };
  return { points: LOCATION_WEIGHT / 3, warning: 'Localisation à vérifier' };
}

export function hasMatchingProfile(employee: ConventionEmployee): boolean {
  return tokenize(`${employee.poste_actuel || ''} ${employee.projet_valide || ''}`).length > 0;
}

function matchOffer(profile: EmployeeProfile, prepared: PreparedOffer): OfferMatch {
  const reasons: string[] = [];
  const warnings: string[] = [];

  const posteRatio = profile.posteTokens.length ? overlap(profile.posteTokens, prepared.title).length / profile.posteTokens.length : 0;
  const projetRatio = profile.projetTokens.length ? overlap(profile.projetTokens, prepared.title).length / profile.projetTokens.length : 0;
  const titleRatio = Math.max(posteRatio, projetRatio);

  if (titleRatio >= 0.99) reasons.push(projetRatio > posteRatio ? 'Correspond au projet validé' : 'Même métier');
  else if (titleRatio > 0) reasons.push(projetRatio > posteRatio ? 'Proche du projet validé' : 'Métier proche');
  else warnings.push('Intitulé de poste différent');

  const location = scoreLocation(profile, prepared);
  if (location.reason) reasons.push(location.reason);
  if (location.warning) warnings.push(location.warning);

  const contentHits = overlap(profile.allTokens, prepared.content);
  const contentRatio = profile.allTokens.length ? contentHits.length / profile.allTokens.length : 0;
  if (contentHits.length > 0) {
    reasons.push(`${contentHits.length} mot${contentHits.length > 1 ? 's' : ''}-clé${contentHits.length > 1 ? 's' : ''} du profil dans l'annonce`);
  }

  return {
    offer: prepared.offer,
    score: Math.round(TITLE_WEIGHT * titleRatio + location.points + CONTENT_WEIGHT * contentRatio),
    reasons,
    warnings,
  };
}

export function rankOffers(employee: ConventionEmployee, prepared: PreparedOffer[], excludedOfferIds: Set<string>): OfferMatch[] {
  const profile = buildProfile(employee);
  if (profile.allTokens.length === 0) return [];
  return prepared
    .filter(p => !excludedOfferIds.has(p.offer.id))
    .map(p => matchOffer(profile, p))
    .filter(m => m.score >= 25)
    .sort((a, b) => b.score - a.score);
}
